/**
 * Lazily split a COARSE `AIContentPositionEntry` into FINE
 * per-node / per-list-item entries, computed in the Post Stream Review (PSR)
 * screen against the live document.
 *
 * Design (see the implementation plan): segmentation is **lazy**. The commit
 * path and non-streamable command handlers each record ONE coarse entry per AI
 * tool call. Only when the author enters step-through mode does the PSR call
 * this helper to expand a coarse entry into fine sub-segments — so positions are
 * always computed fresh against the current doc.
 *
 * Each returned fine entry has the SAME shape as the coarse one
 * (`AIContentPositionEntry`), so every existing toolbar consumer
 * (`buildDiffParamsForSegment`, the undo/redo `replaceRange`, navigation) works
 * unchanged. The per-entry contract is preserved: for each fine entry,
 * `replaceRange(startPos, endPos, originalUserContentSlice)` reconstructs the
 * original content at that spot, and the union of all fine entries equals the
 * coarse change.
 *
 * Fallback: if the coarse entry isn't safely splittable (positions invalid, the
 * pure diff yields 0 or 1 segment, or a slice can't be resolved), we return
 * `[entry]` unchanged so the toolbar behaves exactly like today's single
 * segment.
 */
import type { Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIContentPositionEntry } from '../../../../pm-plugins/ai-session-context/types';

import { segmentSlice } from './segmentSlice';
import type { SliceSegment } from './segmentSlice';

/** Clamp a position into `[0, docSize]`. */
const clamp = (pos: number, docSize: number): number => Math.max(0, Math.min(pos, docSize));

/**
 * Expand a coarse entry into fine per-segment entries.
 *
 * @param editorView - the live editor view (read-only; never mutated here).
 * @param entry - a coarse entry from `aiContentPositions`.
 * @returns fine sub-segments, or `[entry]` when not safely splittable.
 */
export function segmentCommitPositions(
	editorView: EditorView,
	entry: AIContentPositionEntry,
): AIContentPositionEntry[] {
	if (
		!entry ||
		!entry.newSlice ||
		!entry.originalUserContentSlice ||
		entry.startPos == null ||
		entry.endPos == null
	) {
		return [entry];
	}

	// A coarse entry that has been undone currently shows ORIGINAL content at
	// [startPos, endPos]; splitting against the new-content offsets would point
	// at the wrong live ranges. Step-through of an undone coarse entry falls
	// back to the single coarse entry (re-split happens once it's redone / the
	// modal re-opens). This keeps positions correct without guessing.
	if (entry.isUndone) {
		return [entry];
	}

	let sliceSegments: SliceSegment[];
	try {
		sliceSegments = segmentSlice(entry.originalUserContentSlice, entry.newSlice);
	} catch {
		return [entry];
	}

	// Nothing meaningful to split into — keep the single coarse entry.
	if (sliceSegments.length <= 1) {
		return [entry];
	}

	const docSize = editorView.state.doc.content.size;

	// The coarse entry's new content occupies [startPos, endPos] in the live
	// doc. `SliceSegment.newFrom/newTo` are offsets into `entry.newSlice.content`
	// (i.e. relative to `startPos`). `originalFrom/originalTo` are offsets into
	// `entry.originalUserContentSlice.content`, used only to size each fine
	// entry's original slice — they don't map to live positions (that original
	// content isn't currently in the doc).
	const fine: AIContentPositionEntry[] = [];

	for (const seg of sliceSegments) {
		const absStart = clamp(entry.startPos + seg.newFrom, docSize);
		const absEnd = clamp(entry.startPos + seg.newTo, docSize);

		// Defensive: a fine range must stay within the coarse range and be
		// well-ordered. If anything is off, bail out to the coarse entry so we
		// never produce an invalid range that `replaceRange` would throw on.
		if (absEnd < absStart || absStart < entry.startPos || absEnd > entry.endPos) {
			return [entry];
		}

		const originalSlice: Slice = seg.originalSlice;
		const newSlice: Slice = seg.newSlice;

		fine.push({
			startPos: absStart,
			endPos: absEnd,
			originalUserContentSlice: originalSlice,
			newSlice,
			kind: seg.kind,
			// Fine children inherit the parent's undo state. (Guarded above:
			// only reached when the coarse entry is in its AI-applied form.)
			isUndone: entry.isUndone,
		});
	}

	return fine.length > 0 ? fine : [entry];
}
